import { useMemo } from 'react'
import * as React from 'react'
import { useIntl, FormattedMessage, defineMessages } from 'react-intl'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import LayoutContainer from 'components/Common/LayoutContainer'
import TrackedCardCarousel from 'components/Common/TrackedCardCarousel/TrackedCardCarousel'
import CruiseDestinationsSectionItem from './CruiseDestinationsSectionItem'
import TrackingContext from 'contexts/trackingContext'
import qs from 'qs'
import Heading from 'LuxKit/Typography/Heading'

const italicFormatter = (chunks: React.ReactNode) => <i>{chunks}</i>

const departureCityMessages = defineMessages({
  sydney: {
    id: 'CA-Yaslfw6IClD1ys9kw',
    defaultMessage: 'Sydney',
    description: 'Departure city label for the cruises page departures carousel',
  },
  melbourne: {
    id: 'qDpsTB2m7yOk5wdbBmkf',
    defaultMessage: 'Melbourne',
    description: 'Departure city label for the cruises page departures carousel',
  },
  brisbane: {
    id: 'owL6p9Cq5RUX46IGwn0P',
    defaultMessage: 'Brisbane',
    description: 'Departure city label for the cruises page departures carousel',
  },
  rome: {
    id: 't9BldVdrKAZbaK54DgFC',
    defaultMessage: 'Rome',
    description: 'Departure city label for the cruises page departures carousel',
  },
  tokyo: {
    id: 'fwNyyMkmPZjCnIa1czzb',
    defaultMessage: 'Tokyo',
    description: 'Departure city label for the cruises page departures carousel',
  },
  singapore: {
    id: '2ri4HxOmHuqrFPAN8p6j',
    defaultMessage: 'Singapore',
    description: 'Departure city label for the cruises page departures carousel',
  },
})

type DepartureCityId = keyof typeof departureCityMessages

interface DepartureData {
  cityId: DepartureCityId
  image: { id: string }
  filter: string
}

const DEPARTURE_DATA: Array<DepartureData> = [
  {
    cityId: 'sydney',
    image: { id: 'v4kwli3fa6d30n2w1rk9' },
    filter: qs.stringify({
      departureIds: 'le_acf06cdd9c744f969958e1f085554c8b',
      departureNames: 'Sydney',
    }),
  },
  {
    cityId: 'melbourne',
    image: { id: '3wgitcdr0mdfwtfa02ve' },
    filter: qs.stringify({
      departureIds: 'le_bbaa9d6a1445eac881750bea6053f564',
      departureNames: 'Melbourne',
    }),
  },
  {
    cityId: 'brisbane',
    image: { id: 'e3shvrl7vnuw84mb3fbj' },
    filter: qs.stringify({
      departureIds: 'le_67e103b0761e60683e83c559be18d40c',
      departureNames: 'Brisbane',
    }),
  },
  {
    cityId: 'rome',
    image: { id: 'wbk1yjnf56awezs1lu' },
    filter: qs.stringify({
      departureIds: 'le_09d37c08f7b129e96277388757530c72,le_675c2bf8873ef7dc8e8b6ba66366a158',
      departureNames: 'Rome',
    }),
  },
  {
    cityId: 'tokyo',
    image: { id: 'hrl9agi1rgthavmoqtdp' },
    filter: qs.stringify({
      departureIds: 'le_cdd0500dc0ef6682fa6ec6d2e6b577c4',
      departureNames: 'Tokyo',
    }),
  },
  {
    cityId: 'singapore',
    image: { id: 'e83ps9azewdouwruvet' },
    filter: qs.stringify({
      departureIds: 'le_ce393994e8cf430867408678d1a5a9c8',
      departureNames: 'Singapore',
    }),
  },
]

function CruisePageDeparturesSection() {
  const intl = useIntl()

  const departures = useMemo(() => DEPARTURE_DATA.map((departure) => ({
    ...departure,
    name: intl.formatMessage(
      {
        id: 'SHoh_R-xceawVPI8MzxD',
        defaultMessage: 'Cruises from {city}',
        description: 'Tile heading for cruises departing from a specific city',
      },
      { city: intl.formatMessage(departureCityMessages[departure.cityId]) },
    ),
  })), [intl])

  const viewOffersLabel = intl.formatMessage({
    id: 'pzKfqt33bAHKG9r85Qts',
    defaultMessage: 'View offers',
    description: 'Call to action button text to view cruise offers from a departure port',
  })

  return (
    <TrackingContext.Provider
      value={{
        category: 'cruise_departures',
        label: 'Cruise Page Departures',
        listId: 'cruise-departures-carousel',
        listName: 'Cruise Page Departures',
      }}
    >
      <Group gap={32} direction="vertical">
        <LayoutContainer>
          <Heading variant="heading3">
            <FormattedMessage
              id="PUtT163WKS7TFTaTIOAI"
              defaultMessage="Top ports to start your <i>adventure</i>"
              description="Section heading for top cruise departure ports in the cruises section"
              values={{ i: italicFormatter }}
            />
          </Heading>
        </LayoutContainer>

        <LayoutContainer>
          <TrackedCardCarousel pageSize={6} width={rem(180)}>
            {departures.map((departure, index) => (
              <CruiseDestinationsSectionItem
                key={departure.image.id}
                destination={departure}
                position={index}
                href={`/search/cruises?${departure.filter}`}
                image={departure.image}
                heading={departure.name}
                subHeading=""
                align="top"
                callToAction={viewOffersLabel}
              />
            ))}
          </TrackedCardCarousel>
        </LayoutContainer>
      </Group>
    </TrackingContext.Provider>
  )
}

export default CruisePageDeparturesSection
